import { useSyncExternalStore } from "react";
import { BRAND, type Site } from "@neus/shared";
import { useSiteVersion } from "../../api/hooks";
import { CONTENT, shown } from "../../content";
import { MAIN_NAV, PHONE_HREF, STORE_HOURS } from "../../nav";
import { ContentLinkView, RichText } from "../content/RichText";
import { FacebookIcon, InstagramIcon } from "../Icons";
import styles from "./Layout.module.css";
import { Logo } from "./Logo";

declare const __BUILD_YEAR__: number;

/** A footer column from the brand's site.json. */
function FooterColumn({ column }: { column: Site["footer"]["columns"][number] }) {
  switch (column.type) {
    case "nav":
    case "links": {
      const links =
        column.type === "nav"
          ? MAIN_NAV.map((item) => ({ label: item.label, to: item.to }))
          : column.links.filter(shown);
      return (
        <nav aria-label={column.title}>
          <h3>{column.title}</h3>
          <ul className={styles.footerLinks}>
            {links.map((link) => (
              <li key={link.label}>
                <ContentLinkView link={link} />
              </li>
            ))}
          </ul>
        </nav>
      );
    }
    case "hours":
      return (
        <div>
          <h3>{column.title}</h3>
          <dl className={styles.hours}>
            {STORE_HOURS.map((h) => (
              <div key={h.days}>
                <dt>{h.days}</dt>
                <dd>{h.hours}</dd>
              </div>
            ))}
          </dl>
        </div>
      );
    case "contact":
      return (
        <div>
          <h3>{column.title}</h3>
          <p>
            <a href={PHONE_HREF}>{BRAND.phone}</a>
          </p>
          {column.links.filter(shown).map((link) => (
            <p key={link.label}>
              <ContentLinkView link={link} />
            </p>
          ))}
        </div>
      );
  }
}

const neverChanges = () => () => {};
const currentYear = () => new Date().getFullYear();
/** In a pre-rendered page's HTML, so hydrating matches it; the browser's year follows. */
const buildYear = () => __BUILD_YEAR__;

export function Footer() {
  const { data } = useSiteVersion();
  const version = data?.version;
  const year = useSyncExternalStore(neverChanges, currentYear, buildYear);
  return (
    <footer>
      <div className={styles.footer}>
        <div className={`container ${styles.footerGrid}`}>
          <div>
            <Logo variant="footer" />
            <p className={styles.footerBlurb}>
              <RichText text={CONTENT.site.footer.blurb} />
            </p>
            <div className={styles.social}>
              {BRAND.social.facebook && (
                <a href={BRAND.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
                  <FacebookIcon size={20} alt="Facebook" />
                </a>
              )}
              {BRAND.social.instagram && (
                <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
                  <InstagramIcon size={20} alt="Instagram" />
                </a>
              )}
            </div>
          </div>

          {CONTENT.site.footer.columns.map((col) => (
            <FooterColumn key={col.title} column={col} />
          ))}
        </div>
      </div>
      <div className={styles.socket}>
        <div className={`container ${styles.socketInner}`}>
          {/* One text node with the name, so it renders exactly as the fixed text did. */}
          <span>
            {`© Copyright - ${BRAND.legalName} `}
            {year}
          </span>
          {/* Which release is deployed (docs/development/README.md), from the API, so it's empty until that answers. */}
          <span className={styles.version} title="Site version">
            {version && `v${version}`}
          </span>
        </div>
      </div>
    </footer>
  );
}
